<template>
  <div class="site-selector">
    <div class="selector-toolbar">
      <b-field class="search-field">
        <b-input
          class="search-input"
          :placeholder="$t('search_ellipsis')"
          type="search"
          icon="magnify"
          rounded
          v-model="searchField"
        ></b-input>
      </b-field>
      <div class="selection-summary">
        <span>{{ selectedCount }} selected</span>
        <span v-if="primarySiteName" class="summary-primary"
          >Primary: {{ primarySiteName }}</span
        >
      </div>
    </div>

    <div class="site-grid" v-if="sites.length > 0">
      <button
        type="button"
        class="site"
        :class="{
          'selected-site': selectedSites.includes(site._id),
          'primary-site': selectedSites[0] === site._id,
        }"
        v-for="site in sites"
        :key="site._id"
        @click="handleSelectSite(site)"
      >
        <span class="site-type">
          <template v-if="selectedSites[0] === site._id">
            {{ $t('primary') }}
          </template>
          <template v-else-if="selectedSites.includes(site._id)">
            {{ $t('secondary') }}
          </template>
        </span>
        <span class="site-title">{{ site.siteName }}</span>
      </button>
    </div>

    <div v-else class="empty-state">No locations match your search.</div>
  </div>
</template>

<script>
export default {
  name: 'select-sites',
  emits: ['selectedSites'],
  props: {
    initialSelectedSiteIds: {
      type: [Array, String],
    },
  },
  data() {
    return {
      selectedSites: [],
      searchField: '',
    };
  },
  computed: {
    sites() {
      const { sites } = this.$store.state;
      const normalizedSearch = (this.searchField || '').toLowerCase();
      const filteredSites = sites.filter((site) => {
        const searchSiteName = (site.search_sitename || '').toLowerCase();
        const siteName = (site.siteName || '').toLowerCase();
        return (
          searchSiteName.includes(normalizedSearch) ||
          siteName.includes(normalizedSearch)
        );
      });
      filteredSites.sort((a, b) => (a.siteName > b.siteName ? 1 : -1));
      return filteredSites;
    },
    selectedSiteIds() {
      return this.selectedSites.filter((siteId) => Boolean(siteId));
    },
    selectedCount() {
      return this.selectedSiteIds.length;
    },
    primarySiteName() {
      const primarySiteId = this.selectedSites[0];
      if (!primarySiteId) {
        return '';
      }

      const primarySite = this.$store.state.sites.find(
        (site) => site._id === primarySiteId
      );

      return primarySite ? primarySite.siteName : '';
    },
  },
  async created() {
    if (!this.sites || this.sites.length === 0) {
      await this.$store.dispatch('getSites');
    }
    const initialIds = Array.isArray(this.initialSelectedSiteIds)
      ? this.initialSelectedSiteIds
      : [];

    if (initialIds.length > 0) {
      initialIds.forEach((initialSelectedSiteId) => {
        const initialSite = this.sites.find(
          (item) => item._id === initialSelectedSiteId
        );
        if (initialSite) {
          this.selectedSites.push(initialSite._id);
        }
      });

      this.$forceUpdate();
    }
  },
  methods: {
    handleSelectSite(site) {
      if (this.selectedSites.includes(site._id)) {
        const index = this.selectedSites.findIndex((item) => item === site._id);
        if (index === 0) {
          this.selectedSites[index] = null;
        } else {
          this.selectedSites.splice(index, 1);
        }
      } else if (this.selectedSites[0] === null) {
        this.selectedSites[0] = site._id;
      } else {
        this.selectedSites.push(site._id);
      }

      this.$emit('selectedSites', this.selectedSites);

      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
.site-selector {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
}

.selector-toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.search-field {
  display: flex;
  justify-content: center;
  margin-bottom: 0;
}

.search-input {
  width: 100%;
  max-width: 560px;
}

.selection-summary {
  display: flex;
  justify-content: center;
  gap: 20px;
  color: gray;
  font-size: 14px;
}

.summary-primary {
  font-weight: 600;
}

.site-grid {
  overflow: auto;
  max-height: calc(100vh - 320px);
  margin: 10px 0;
  padding: 6px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  grid-auto-rows: 110px;
  align-content: start;
  align-items: stretch;
  gap: 16px;
}

.site {
  position: relative;
  box-shadow: 0px 0px 5px lightgrey;
  color: gray;
  width: 100%;
  height: 110px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  background-color: white;
  border: 1px solid lightgrey;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    border: 2px solid $icon-active;
  }
}

.selected-site {
  border: 2px solid $icon-active;

  p {
    color: $icon-active;
  }
}

.primary-site {
  border: 2px solid rgb(30, 205, 30);

  p {
    color: rgb(30, 205, 30);
  }
}

.site-type {
  position: absolute;
  top: 8px;
  right: 8px;
  color: gray;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.site-title {
  text-align: center;
  font-weight: 600;
  line-height: 1.2;
}

.empty-state {
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: gray;
  border: 1px dashed lightgrey;
  border-radius: 8px;
}
</style>
